import { Link } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useAppState } from '../context/AppContext';
import { fetchInspections, inspectionImageUrl } from '../services/api';
import {
  IconChevronRight,
  IconListDocs,
  IconScanFrame,
  IconUsers,
} from '../components/Icons';
import './HomePage.css';

function isSameDay(iso, ref = new Date()) {
  if (!iso) return false;
  const d = new Date(iso);
  return (
    d.getFullYear() === ref.getFullYear() &&
    d.getMonth() === ref.getMonth() &&
    d.getDate() === ref.getDate()
  );
}

function titleOf(row) {
  return (
    row.meta?.pdr ||
    row.meta?.rollNo ||
    row.meta?.customerName ||
    row.meta?.sup ||
    'ผลสแกน'
  );
}

export default function HomePage() {
  const { rules } = useAppState();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchInspections(100)
      .then((data) => {
        if (alive) setRows(data || []);
      })
      .catch((err) => {
        if (alive) setError(err.message || 'โหลด Dashboard ไม่สำเร็จ');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const stats = useMemo(() => {
    const total = rows.length;
    const ok = rows.filter((r) => r.verdict === 'OK').length;
    const ng = rows.filter((r) => r.verdict === 'NG').length;
    const today = rows.filter((r) => isSameDay(r.createdAt)).length;
    const okRate = total ? Math.round((ok / total) * 100) : 0;
    return { total, ok, ng, today, okRate };
  }, [rows]);

  const recent = rows.slice(0, 5);
  const host = typeof window !== 'undefined' ? window.location.hostname : 'YOUR_IP';
  const certUrl = `http://${host}:4010/cert.pem`;

  return (
    <div className="dash stack">
      <header className="dash-hero">
        <div>
          <p className="dash-kicker">ภาพรวมวันนี้</p>
          <h1>Dashboard</h1>
        </div>
        <Link className="btn btn-primary btn-pill" to="/scan">
          สแกนเลย
        </Link>
      </header>

      <section className="dash-stats" aria-label="สรุปผลตรวจ">
        <div className="dash-stat">
          <span className="dash-stat-label">วันนี้</span>
          <strong className="dash-stat-value">{loading ? '—' : stats.today}</strong>
        </div>
        <div className="dash-stat">
          <span className="dash-stat-label">ทั้งหมด</span>
          <strong className="dash-stat-value">{loading ? '—' : stats.total}</strong>
        </div>
        <div className="dash-stat dash-stat-ok">
          <span className="dash-stat-label">ผ่าน</span>
          <strong className="dash-stat-value">{loading ? '—' : stats.ok}</strong>
        </div>
        <div className="dash-stat dash-stat-ng">
          <span className="dash-stat-label">ไม่ผ่าน</span>
          <strong className="dash-stat-value">{loading ? '—' : stats.ng}</strong>
        </div>
      </section>

      {!loading && stats.total > 0 ? (
        <section
          className="dash-progress card"
          aria-label={`อัตราผ่านโดยรวม ${stats.okRate} เปอร์เซ็นต์`}
        >
          <div className="dash-progress-head">
            <span>อัตราผ่านโดยรวม</span>
            <strong>{stats.okRate}%</strong>
          </div>
          <div
            className="dash-progress-track"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={stats.okRate}
          >
            <div
              className={`dash-progress-fill${stats.okRate < 50 ? ' is-low' : stats.okRate < 80 ? ' is-mid' : ''}`}
              style={{ width: `${stats.okRate}%` }}
            />
          </div>
          <p className="dash-progress-meta muted">
            ผ่าน {stats.ok} · ไม่ผ่าน {stats.ng} จาก {stats.total} รายการ
          </p>
        </section>
      ) : null}

      <section className="dash-actions">
        <Link className="dash-action dash-action-primary" to="/scan">
          <span className="dash-action-icon" aria-hidden="true">
            <IconScanFrame size={22} />
          </span>
          <span>
            <strong>เริ่มสแกน</strong>
            <small>ถ่าย / ตรวจผิวกระดาษ</small>
          </span>
          <span className="dash-chevron" aria-hidden="true">
            <IconChevronRight />
          </span>
        </Link>
        <Link className="dash-action" to="/data">
          <span className="dash-action-icon" aria-hidden="true">
            <IconListDocs size={22} />
          </span>
          <span>
            <strong>ดูข้อมูล</strong>
            <small>ประวัติผลสแกนทั้งหมด</small>
          </span>
          <span className="dash-chevron" aria-hidden="true">
            <IconChevronRight />
          </span>
        </Link>
        <Link className="dash-action" to="/customers">
          <span className="dash-action-icon" aria-hidden="true">
            <IconUsers size={22} />
          </span>
          <span>
            <strong>ลูกค้า</strong>
            <small>รายชื่อและกรองข้อมูล</small>
          </span>
          <span className="dash-chevron" aria-hidden="true">
            <IconChevronRight />
          </span>
        </Link>
      </section>

      <section className="card dash-recent">
        <div className="dash-section-head">
          <h2>ล่าสุด</h2>
          <Link className="dash-link" to="/data">
            ทั้งหมด
          </Link>
        </div>

        {loading && <p className="muted">กำลังโหลด...</p>}
        {error && <p style={{ color: 'var(--ng)', margin: 0 }}>{error}</p>}
        {!loading && !error && recent.length === 0 && (
          <div className="dash-empty">
            <p className="muted" style={{ margin: 0 }}>
              ยังไม่มีผลสแกน — กดปุ่มสแกนเพื่อเริ่มตรวจ
            </p>
            <Link className="btn btn-primary" to="/scan">
              เริ่มสแกนครั้งแรก
            </Link>
          </div>
        )}

        <div className="dash-list">
          {recent.map((row) => {
            const img = inspectionImageUrl(row);
            const count = row.spotCount ?? row.spots?.length ?? 0;
            return (
              <Link key={row.id} to={`/scan/result/${row.id}`} className="dash-row">
                {img ? (
                  <img src={img} alt="" className="dash-thumb" />
                ) : (
                  <div className="dash-thumb dash-thumb-empty" aria-hidden="true" />
                )}
                <div className="dash-row-body">
                  <div className="dash-row-top">
                    <strong>{titleOf(row)}</strong>
                    <span className={`badge ${row.verdict === 'OK' ? 'ok' : 'ng'}`}>
                      {row.verdict === 'OK' ? 'ผ่าน' : 'ไม่ผ่าน'}
                    </span>
                  </div>
                  <p className="muted dash-row-meta">
                    {new Date(row.createdAt).toLocaleString()}
                    {` · ${count} จุด`}
                    {row.meta?.grade ? ` · ${row.meta.grade}` : ''}
                  </p>
                </div>
                <span className="dash-chevron" aria-hidden="true">
                  <IconChevronRight size={18} />
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="card dash-rules">
        <h2>เกณฑ์ที่ใช้ตอนนี้</h2>
        <ul className="dash-rules-list">
          <li>จุดด่างดำ Ø ≤ {rules.maxBlackSpotDiameterMm} มม.</li>
          <li>ไม่เกิน {rules.maxBlackSpotsPerSqFt} จุด / ตร.ฟุต (LFB-QAD-SP-004)</li>
          <li>จุดใหญ่กว่า {rules.largeSpotThresholdMm} มม. ห่าง ≥ {rules.minDistanceBetweenLargeSpotsCm} ซม.</li>
          <li>ต้องไม่มีตาไม้ / เปื้อนเศษสี / จุด Calender</li>
          <li>ตาไม้ / เปื้อนเศษสี / Calender = ไม่ผ่าน</li>
        </ul>
      </section>

      <details className="card dash-help">
        <summary>ช่วยเหลือ · ติดตั้งบน iPhone</summary>
        <ol className="muted dash-help-list">
          <li>
            เปิด Safari ไปที่{' '}
            <a href={certUrl} target="_blank" rel="noreferrer">
              ดาวน์โหลด cert
            </a>
          </li>
          <li>อนุญาตติดตั้ง → Settings → Certificate Trust Settings</li>
          <li>เปิด trust สำหรับ Rollscan Local</li>
          <li>Safari → Share → Add to Home Screen</li>
        </ol>
      </details>
    </div>
  );
}
